import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// QUICKTRADE_PILLS (Option A, 2026-10-18): strip TV-style — 2 pill [Bán <bid>] [Mua <ask>]
// + mắt 👁. Click pill = mở ticket (hoặc đặt lệnh ngay nếu one-click được bật trong
// Settings → Execution). Bỏ collapse / stepper / ⚡ / ⓘ (A1–A3, A5): qty nhập trong ticket,
// spread + synthetic nằm trong tooltip của 2 pill (A2).
// Pure controlled UI: nhận quote qua props, tương tác qua callbacks.
// KHÔNG import broker/OMS/hooks widget (Rule 9: không chạm VNInvestCharts).
import { useEffect, useState } from "react";
import { useWidgetI18n } from "../../context/WidgetI18nContext";
import { isTypingTarget } from "@vninvestcharts/engine-drawing";
import { resolveMarketProfile } from "../../../lib/core/trading/MarketRules";
import styles from "./QuickTradingHeader.module.css";
function clampLot(quantity, lotSize, maxQuantity) {
    const stepped = Math.max(lotSize, Math.round(quantity / lotSize) * lotSize);
    return maxQuantity != null ? Math.min(stepped, maxQuantity) : stepped;
}
export function QuickTradingHeader({ symbol, quote, defaultQuantity, lotSize = 100, maxQuantity, defaultOneClick = false, onBuy, onSell, onOpenTicket, collapsible = false, defaultCollapsed = false, tradingEnabled = true, onToggleTradingEnabled, disabled = false, spreadUnit = "đ", }) {
    const { t } = useWidgetI18n();
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    const hasQuote = quote != null;
    const inactive = disabled || !hasQuote;
    // UIUX S4: Shift+T thu gon/mo rong (khong dung drawing/trading hotkey).
    useEffect(() => {
        if (!collapsible)
            return;
        const onKey = (e) => {
            if (e.type !== "keydown" || isTypingTarget(e))
                return;
            if (!e.shiftKey || e.ctrlKey || e.metaKey || e.altKey)
                return;
            if (e.key.toUpperCase() !== "T")
                return;
            e.preventDefault();
            setCollapsed((c) => !c);
        };
        // eslint-disable-next-line no-restricted-globals -- global Shift+T hotkey (same pattern as OHLCHeader replay hotkeys; SSR-safe: effect only)
        window.addEventListener("keydown", onKey);
        // eslint-disable-next-line no-restricted-globals -- symmetric cleanup of the listener above
        return () => window.removeEventListener("keydown", onKey);
    }, [collapsible]);
    if (collapsible && collapsed) {
        return (_jsx("div", { className: styles.header, "data-testid": "quick-trading-header", children: _jsxs("button", { type: "button", className: styles.expandBtn, "data-testid": "quicktrade-expand", onClick: () => setCollapsed(false), title: "Shift+T", children: [_jsx("span", { children: t("widget.trading.sell", { defaultValue: "Sell" }) }), _jsx("span", { "aria-hidden": "true", children: "/" }), _jsx("span", { children: t("widget.trading.buy", { defaultValue: "Buy" }) })] }) }));
    }
    const handleSide = (side) => {
        if (inactive)
            return;
        // A3: stepper đã bỏ — qty lấy từ lot mặc định; chỉnh số lượng trong ticket.
        const qty = clampLot(defaultQuantity ?? lotSize, lotSize, maxQuantity);
        if (defaultOneClick) {
            if (side === "buy")
                onBuy?.(qty);
            else
                onSell?.(qty);
            return;
        }
        if (onOpenTicket) {
            onOpenTicket(side, qty);
            return;
        }
        if (side === "buy")
            onBuy?.(qty);
        else
            onSell?.(qty);
    };
    // F-02c: số lẻ theo tickSize của mã (VN tick 10/50/100 -> 0 lẻ, crypto 0.01 -> 2 lẻ),
    // không gán cứng theo magnitude (BTC tick 0.1 -> 1 lẻ cho tới khi PM chốt Q1).
    // Fallback magnitude khi tick không hợp lệ để không vỡ render.
    const formatPrice = (value) => {
        const tick = resolveMarketProfile(symbol).getTickSize(value);
        const dp = Number.isFinite(tick) && tick > 0
            ? Math.max(0, Math.ceil(-Math.log10(tick) - 1e-9))
            : value >= 1000
                ? 1
                : value >= 1
                    ? 2
                    : 4;
        return new Intl.NumberFormat("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(value);
    };
    // Spread unit follows the market (crypto quote currency, VN đồng default).
    const effectiveSpreadUnit = spreadUnit && spreadUnit !== "đ" ? spreadUnit : /(USDT|USDC)$/.test(symbol) ? "USDT" : "đ";
    const spreadInfo = (() => {
        if (quote == null || quote.lastPrice <= 0 || !Number.isFinite(quote.spread) || quote.spread < 0) {
            return null;
        }
        const bps = (quote.spread / quote.lastPrice) * 10000;
        return {
            text: `${quote.spread.toLocaleString("en-US")} ${effectiveSpreadUnit}`,
            title: `${t("widget.trading.spread", { defaultValue: "Spread" })}: ${quote.spread} ${effectiveSpreadUnit} (${bps.toFixed(1)} bps)`,
        };
    })();
    // A2: spread + synthetic GỘP vào tooltip của CẢ HAI pill (thay chip DOM cũ).
    const pillTitle = spreadInfo == null
        ? undefined
        : quote?.isSyntheticSpread
            ? `${spreadInfo.title} · ${t("widget.trading.syntheticQuoteHint", {
                defaultValue: "Synthetic Bid/Ask derived from candles (not a real order book)",
            })}`
            : spreadInfo.title;
    return (_jsxs("div", { className: styles.header, "data-testid": "quick-trading-header", children: [_jsxs("button", { type: "button", className: styles.sellBtn, "data-testid": "sell-btn", disabled: inactive, onClick: () => handleSide("sell"), title: pillTitle, children: [_jsx("span", { children: t("widget.trading.sell", { defaultValue: "Sell" }) }), _jsx("span", { className: styles.price, "data-testid": "bid-price", children: hasQuote ? formatPrice(quote.bidPrice) : t("widget.trading.noQuote", { defaultValue: "No quote" }) })] }), _jsxs("button", { type: "button", className: styles.buyBtn, "data-testid": "buy-btn", disabled: inactive, onClick: () => handleSide("buy"), title: pillTitle, children: [_jsx("span", { children: t("widget.trading.buy", { defaultValue: "Buy" }) }), _jsx("span", { className: styles.price, "data-testid": "ask-price", children: hasQuote ? formatPrice(quote.askPrice) : t("widget.trading.noQuote", { defaultValue: "No quote" }) })] }), collapsible && (_jsx("button", { type: "button", className: styles.toggle, "data-testid": "quicktrade-collapse", onClick: () => setCollapsed(true), title: "Shift+T", "aria-label": "Shift+T", children: "»" })), _jsx("button", { type: "button", className: `${styles.toggle} ${styles.eye}`, "data-testid": "paper-visibility-toggle", "aria-pressed": tradingEnabled, disabled: disabled, onClick: () => onToggleTradingEnabled?.(!tradingEnabled), title: tradingEnabled
                    ? t("widget.trading.hidePaperTrading", { defaultValue: "Hide paper trading (re-enable in Settings)" })
                    : t("widget.trading.showPaperTrading", { defaultValue: "Show paper trading" }), children: tradingEnabled ? "👁" : "🚫" })] }));
}
